import axios from 'axios';
import { API_BASE_URL } from '../config';

export interface CsvUploadJobProgress {
  phase?: string;
  percent?: number;
  totalUrls?: number;
  processedUrls?: number;
}

export interface CsvUploadJobStatus {
  jobId: string;
  fileName: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress?: CsvUploadJobProgress;
  error?: string;
  result?: {
    httpStatus: number;
    body: any;
  };
}

export class UploadCancelledByUserError extends Error {
  constructor(message = 'Upload cancelled') {
    super(message);
    this.name = 'UploadCancelledByUserError';
  }
}

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Poll a background CSV upload job until it completes, fails, or is cancelled.
 */
export async function pollCsvUploadJob(
  jobId: string,
  token: string,
  onProgress?: (progress: CsvUploadJobProgress) => void,
  shouldCancel?: () => boolean
): Promise<{ httpStatus: number; body: any }> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < MAX_POLL_MS) {
    if (shouldCancel?.()) {
      throw new UploadCancelledByUserError();
    }

    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

    if (shouldCancel?.()) {
      throw new UploadCancelledByUserError();
    }

    const response = await axios.get<{ success: boolean; data: CsvUploadJobStatus }>(
      `${API_BASE_URL}/upload/csv/status/${jobId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const job = response.data.data;

    if (job.progress && onProgress) {
      onProgress(job.progress);
    }

    if (job.status === 'COMPLETED' && job.result) {
      return job.result;
    }

    if (job.status === 'CANCELLED') {
      throw new UploadCancelledByUserError(job.error || 'Upload cancelled');
    }

    if (job.status === 'FAILED') {
      const message =
        job.error ||
        job.result?.body?.error?.message ||
        job.result?.body?.message ||
        'Upload processing failed';
      const details = job.result?.body?.error?.details;
      const err = new Error(details ? `${message} ${details}` : message);
      (err as any).response = { data: job.result?.body, status: job.result?.httpStatus || 500 };
      throw err;
    }
  }

  throw new Error(
    'Upload is still processing but polling timed out. Check Data Management in a few minutes.'
  );
}

/** Ask the backend to stop processing this upload job. */
export async function cancelCsvUploadJob(jobId: string, token: string): Promise<void> {
  await axios.post(
    `${API_BASE_URL}/upload/csv/cancel/${jobId}`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
}
